import type { InfiniteData } from "@tanstack/react-query";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { Spinner } from "@/components/shared/spinner";
import { UserContext } from "@/contexts/user-context";
import type { UserAssociation } from "@/graphql/codegen/graphql";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer";
import {
  DISCOVER_WIDGET_CACHE_MAX_AGE_MS,
  type DiscoverWidgetApiResponse,
  type DiscoverWidgetItem,
  type DiscoverWidgetPreset,
  type DiscoverWidgetTimePeriod,
  getDiscoverWidgetApiUrl,
  getDiscoverWidgetCacheMaxAgeMs,
  getDiscoverWidgetQueryKey,
} from "@/utils/discover-widget";

const getItemPublicKey = (item: DiscoverWidgetItem) => {
  if ("coinPublicKey" in item) {
    return item.coinPublicKey;
  }

  if ("publicKey" in item) {
    return item.publicKey;
  }

  return item.publicKeyBase58Check;
};

const getBlockedPublicKeys = (blockedUsers: UserAssociation[]) =>
  new Set(
    blockedUsers
      .map((association) => association.target?.publicKey)
      .filter((publicKey): publicKey is string => Boolean(publicKey)),
  );

const fetchDiscoverWidgetPage = async ({
  pageParam,
  preset,
  refresh,
  signal,
  timePeriod,
}: {
  pageParam?: string | null;
  preset: DiscoverWidgetPreset;
  refresh?: boolean;
  signal?: AbortSignal;
  timePeriod: DiscoverWidgetTimePeriod;
}) => {
  const response = await fetch(
    getDiscoverWidgetApiUrl({
      pageParam,
      preset,
      refresh,
      timePeriod,
    }),
    {
      headers: {
        Accept: "application/json",
      },
      signal,
    },
  );

  if (!response.ok) {
    throw new Error("Failed to load discover widget");
  }

  return (await response.json()) as DiscoverWidgetApiResponse;
};

export const useRefreshDiscoverWidgetCategory = ({
  preset,
  timePeriod,
}: {
  preset: DiscoverWidgetPreset;
  timePeriod: DiscoverWidgetTimePeriod;
}) => {
  const queryClient = useQueryClient();
  const queryKey = getDiscoverWidgetQueryKey({ preset, timePeriod });
  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    setRefreshing(true);

    try {
      const firstPage = await fetchDiscoverWidgetPage({
        pageParam: null,
        preset,
        refresh: true,
        timePeriod,
      });

      queryClient.setQueryData<
        InfiniteData<DiscoverWidgetApiResponse, string | null>
      >(queryKey, {
        pageParams: [null],
        pages: [firstPage],
      });
    } finally {
      setRefreshing(false);
    }
  }, [preset, queryClient, queryKey, timePeriod]);

  return {
    refresh,
    refreshing,
  };
};

export const useDiscoverWidgetQuery = ({
  preset,
  spinner = <Spinner />,
  timePeriod,
}: {
  preset: DiscoverWidgetPreset;
  spinner?: ReactNode;
  timePeriod: DiscoverWidgetTimePeriod;
}) => {
  const { blockedUsers } = useContext(UserContext);
  const queryKey = getDiscoverWidgetQueryKey({ preset, timePeriod });
  const { isIntersecting, ref } = useIntersectionObserver({
    rootMargin: "100px",
    threshold: 0.5,
  });
  const query = useInfiniteQuery({
    gcTime: DISCOVER_WIDGET_CACHE_MAX_AGE_MS,
    getNextPageParam: (lastPage: DiscoverWidgetApiResponse) =>
      lastPage.nextPageParam,
    initialPageParam: null as string | null,
    meta: {
      suppressGlobalErrorToast: true,
    },
    queryFn: ({ pageParam, signal }) =>
      fetchDiscoverWidgetPage({
        pageParam,
        preset,
        signal,
        timePeriod,
      }),
    queryKey,
    staleTime: getDiscoverWidgetCacheMaxAgeMs(timePeriod),
  });
  const blockedPublicKeys = useMemo(
    () => getBlockedPublicKeys(blockedUsers),
    [blockedUsers],
  );
  const rawItems = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data?.pages],
  );
  const items = useMemo(
    () =>
      rawItems.filter((item) => {
        const publicKey = getItemPublicKey(item);

        return !publicKey || !blockedPublicKeys.has(publicKey);
      }),
    [blockedPublicKeys, rawItems],
  );
  const initialLoading = !query.data && query.isPending;
  const loadingMore = query.isFetchingNextPage;

  useEffect(() => {
    if (
      isIntersecting &&
      query.hasNextPage &&
      !query.isFetchingNextPage &&
      !query.isFetching
    ) {
      void query.fetchNextPage();
    }
  }, [
    isIntersecting,
    query.fetchNextPage,
    query.hasNextPage,
    query.isFetching,
    query.isFetchingNextPage,
  ]);

  const loadingMarkup = query.hasNextPage ? (
    <div ref={ref} className="flex h-[30px] items-center justify-center p-4">
      {(initialLoading || loadingMore) && spinner}
    </div>
  ) : initialLoading ? (
    <Spinner className="my-2 flex justify-center" size={24} />
  ) : null;

  return {
    initialLoading,
    items,
    loadingMarkup,
    loadingMore,
    query,
  };
};
